import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import useFormInstance from "antd/es/form/hooks/useFormInstance";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import Located from "@mapmycustomers/shared/types/base/Located";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { isPartiallyMatchedGeoAddress } from "@mapmycustomers/shared/util/geo/isPartiallyMatchedGeoAddress";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { FormItem, ManualAddress } from "@mapmycustomers/ui";

import { EnterPinLocationModePayload } from "@app/scene/map/store/pinLocation/actions";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import IncompleteAddressAlert from "../IncompleteAddressAlert";
import ChangePinLocationModal from "../MapPopup/ChangePinLocationModal";

import AddressChangeAlert from "./AddressChangeAlert";
import styles from "./AddressFormItem.module.scss";
import AddressLabel from "./AddressLabel";
import messages from "./messages";

interface Props {
  disabled?: boolean;
  entity: EntityWithLocation;
  formChanged: boolean;
  isMapMode: boolean;
  onAddressChange: (located: Located) => void;
  onChange?: (changed: boolean) => void;
  onEnterPinLocationMode?: (payload: EnterPinLocationModePayload) => void;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  required?: boolean;
}

const AddressFormItem: React.FC<Props> = ({
  disabled,
  entity,
  formChanged,
  isMapMode,
  onAddressChange,
  onChange,
  onEnterPinLocationMode,
  onReverseGeocodeAddress,
  required,
}) => {
  const intl = useIntl();
  const form = useFormInstance();

  const [addressChanged, setAddressChanged] = useState<boolean>(false);
  const [coordinatesChanged, setCoordinatesChanged] = useState<boolean>(false);
  const [changePinLocationVisible, showChangePinLocationModal, hideChangePinLocationModal] =
    useBoolean(false, true);
  const [alertVisible, setAlertVisible] = useState<boolean>(
    isPartiallyMatchedGeoAddress(entity.geoAddress, entity.geoSource)
  );

  const handleUpdateAddress = useCallback(
    (address: Address) => {
      const values = form.getFieldsValue();
      const updatedAddress = { ...values.addressDetails, ...address };
      form.setFieldValue("addressDetails", updatedAddress);
      setAlertVisible(false);
      onChange?.(true);
    },
    [form, onChange, setAlertVisible]
  );

  const addressField = getFieldModelByEntityType(entity.entity)?.getByName("address");
  const isEditable = addressField?.isEditable ?? false;

  const handleUndo = useCallback(() => {
    form.resetFields(["addressDetails", "latitude", "longitude"]);
    setAddressChanged(false);
    setCoordinatesChanged(false);
  }, [form]);

  useEffect(() => {
    setAlertVisible(!!entity.geoAddress?.partial_match);
  }, [entity, setAlertVisible]);

  useEffect(() => {
    if (!formChanged) {
      setCoordinatesChanged(false);
      setAddressChanged(false);
    }
  }, [formChanged]);

  return (
    <Tooltip title={intl.formatMessage(messages.nonEditable)} trigger={isEditable ? [] : ["hover"]}>
      <FormItem
        className={styles.formItem}
        label={intl.formatMessage(messages.label)}
        name="addressDetails"
        required={required}
      >
        <ManualAddress
          disabled={disabled}
          editMode
          extra={
            <AddressLabel
              coordinatesChanged={coordinatesChanged}
              disabled={disabled}
              entity={entity}
              form={form}
              formChanged={formChanged}
              isMapMode={isMapMode}
              onChange={onChange}
              onEnterPinLocationMode={onEnterPinLocationMode}
              onSetAddressChanged={setAddressChanged}
              onSetCoordinatesChanged={setCoordinatesChanged}
              onShowChangePinLocationModal={showChangePinLocationModal}
              required={required}
            />
          }
          label={intl.formatMessage(messages.label)}
          onReverseGeocodeAddress={onReverseGeocodeAddress}
          required={required}
        />
      </FormItem>
      {coordinatesChanged && (
        <AddressChangeAlert addressChanged={addressChanged} handleUndo={handleUndo} />
      )}
      {alertVisible && !disabled && (
        <IncompleteAddressAlert
          className={styles.addressAlert}
          entity={entity}
          onOk={handleUpdateAddress}
          showCancelConfirmation
        />
      )}
      {changePinLocationVisible && (
        <ChangePinLocationModal
          // we can't show the modal if entity is undefined
          entity={entity!}
          onHide={hideChangePinLocationModal}
          onUpdate={onAddressChange}
        />
      )}
    </Tooltip>
  );
};

const mapDispatchToProps = {
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(null, mapDispatchToProps)(AddressFormItem);
